import { useEffect, useState, useRef, useCallback } from "react";
import HearingTestButton from "../../../components/hearingTestButton/hearingTestButton";
import PillTitle from "../../../components/pillTitle/pillTitle";
import { useI18n } from "../../../utils/translate/i18n/useI18n";
import type { Ear } from "../../../type/tonalTest";
import type { EarProgress } from "../../../type/test";
import { TONAL_SIGNALS_PER_LEVEL } from "../../../utils/config";
import { dbToVolume } from "../../../utils/audioLevels";

const dBLevels = Array.from({ length: 25 }, (_, i) => i * 5); // 0–120 co 5, * 10 - co 10
const volumes = dBLevels.map(dbToVolume);
const LEGACY_STARTING_DB_LEVEL = 0;
const PROFESSIONAL_STARTING_DB_LEVEL = 30;
const LEGACY_STARTING_VOLUME_INDEX = dBLevels.indexOf(LEGACY_STARTING_DB_LEVEL);
const PROFESSIONAL_STARTING_VOLUME_INDEX = dBLevels.indexOf(PROFESSIONAL_STARTING_DB_LEVEL);

const PULSE_ON = 0.5;
const PULSE_OFF = 0.5;
const STEP_DURATION = TONAL_SIGNALS_PER_LEVEL * (PULSE_ON + PULSE_OFF);
const PAUSE_DURATION = 1500; // ms pause after tone before advancing
const HEARD_PAUSE_DURATION = 1000; // ms pause after clicking "heard" before advancing

type EarResults = Record<Ear, number[]>;

const createFrequencyOrder = (length: number) => {
  const order = Array.from({ length }, (_, index) => index);

  for (let index = order.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [order[index], order[swapIndex]] = [order[swapIndex], order[index]];
  }

  return order;
};

const initialProgress = (startingVolumeIndex: number): Record<Ear, EarProgress> => ({
  left: { freqIndex: 0, volIndex: startingVolumeIndex, mode: "descending" },
  right: { freqIndex: 0, volIndex: startingVolumeIndex, mode: "descending" }
});

export default function TonalTestPanel({
  ear,
  frequencies,
  onComplete,
  onCancel,
  continuous = false,
  addNoise = false,
  randomMode = false,
  professionalMode = false,
  user = undefined,
}: {
  ear: Ear;
  frequencies: number[];
  onComplete: (results: EarResults) => void;
  onCancel: (partial: Record<Ear, number[]>) => void;
  continuous?: boolean;
  addNoise?: boolean;
  randomMode?: boolean;
  professionalMode?: boolean;
  user?: { admin?: boolean } | null;
}) {
  const { t } = useI18n();
  const startingVolumeIndex = professionalMode ? PROFESSIONAL_STARTING_VOLUME_INDEX : LEGACY_STARTING_VOLUME_INDEX;
  const [frequencyOrderByEar] = useState<Record<Ear, number[]>>(() => ({
    left: professionalMode ? createFrequencyOrder(frequencies.length) : frequencies.map((_, index) => index),
    right: professionalMode ? createFrequencyOrder(frequencies.length) : frequencies.map((_, index) => index)
  }));
  const [activeEar, setActiveEar] = useState<Ear>(() => {
    if (!randomMode) {
      return ear;
    }

    return Math.random() < 0.5 ? "left" : "right";
  });
  const [progressByEar, setProgressByEar] = useState<Record<Ear, EarProgress>>(() => initialProgress(startingVolumeIndex));
  const [resultsByEar, setResultsByEar] = useState<EarResults>({ left: [], right: [] });
  const [isFinished, setIsFinished] = useState(false);
  const [isHeardButtonHidden, setIsHeardButtonHidden] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const activeSourcesRef = useRef<AudioScheduledSourceNode[]>([]);
  const noiseSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const stepHandledRef = useRef(false);

  const currentProgress = progressByEar[activeEar];
  const currentFreqIndex = currentProgress.freqIndex;
  const currentFrequencyIndex = frequencyOrderByEar[activeEar][currentFreqIndex] ?? currentFreqIndex;
  const currentVolIndex = currentProgress.volIndex;
  const currentFrequency = frequencies[currentFrequencyIndex] ?? frequencies[frequencies.length - 1];
  const currentDbLevel = dBLevels[currentVolIndex] ?? dBLevels[dBLevels.length - 1];
  const testedEarCount = randomMode ? 2 : 1;
  const legacyLevelsPerFrequency = dBLevels.length - LEGACY_STARTING_VOLUME_INDEX;
  const totalSteps = professionalMode
    ? frequencies.length * testedEarCount
    : frequencies.length * legacyLevelsPerFrequency * testedEarCount;

  const getEarProgressValue = (targetEar: Ear) => {
    const earProgress = progressByEar[targetEar];

    if (professionalMode) {
      return Math.min(earProgress.freqIndex, frequencies.length);
    }

    if (earProgress.freqIndex >= frequencies.length) {
      return frequencies.length * legacyLevelsPerFrequency;
    }

    return earProgress.freqIndex * legacyLevelsPerFrequency + (earProgress.volIndex - LEGACY_STARTING_VOLUME_INDEX) + 1;
  };

  const progress = isFinished
    ? 100
    : ((getEarProgressValue(randomMode ? "left" : activeEar) + (randomMode ? getEarProgressValue("right") : 0)) /
        totalSteps) *
      100;
    //console.log(user?.admin);
  const stopActiveSources = useCallback(() => {
    activeSourcesRef.current.forEach(src => {
      try { src.stop(); } catch { /* already stopped */ }
    });
    activeSourcesRef.current = [];
  }, []);

  const stopNoise = useCallback(() => {
    if (noiseSourceRef.current) {
      try { noiseSourceRef.current.stop(); } catch { /* already stopped */ }
      noiseSourceRef.current = null;
    }
  }, []);

  const pickNextEar = useCallback((nextProgress: Record<Ear, EarProgress>) => {
    if (!randomMode) {
      return ear;
    }

    const candidates = (Object.keys(nextProgress) as Ear[]).filter(
      targetEar => nextProgress[targetEar].freqIndex < frequencies.length
    );

    if (candidates.length === 0) {
      return null;
    }

    return candidates[Math.floor(Math.random() * candidates.length)];
  }, [ear, frequencies.length, randomMode]);

  const commitStep = useCallback((targetEar: Ear, heardAtDb?: number) => {
    setIsHeardButtonHidden(false);
    const currentEarProgress = progressByEar[targetEar];
    const nextResults: EarResults = {
      left: [...resultsByEar.left],
      right: [...resultsByEar.right]
    };

    const nextProgress = {
      left: { ...progressByEar.left },
      right: { ...progressByEar.right }
    };

    const targetProgress = nextProgress[targetEar];
    const targetFrequencyIndex = frequencyOrderByEar[targetEar][currentEarProgress.freqIndex] ?? currentEarProgress.freqIndex;

    if (professionalMode) {
      const finalizeFrequency = (resultDb: number) => {
        nextResults[targetEar][targetFrequencyIndex] = resultDb;

        targetProgress.freqIndex += 1;
        targetProgress.volIndex = startingVolumeIndex;
        targetProgress.mode = "descending";
        targetProgress.lastHeardDb = undefined;
      };

      if (heardAtDb !== undefined) {
        if (targetProgress.mode === "ascending") {
          finalizeFrequency(heardAtDb);
        } else if (targetProgress.volIndex <= 0) {
          finalizeFrequency(heardAtDb);
        } else {
          targetProgress.lastHeardDb = heardAtDb;
          targetProgress.mode = "descending";
          targetProgress.volIndex -= 1;
        }
      } else if (targetProgress.mode === "descending") {
        if (targetProgress.lastHeardDb !== undefined) {
          finalizeFrequency(targetProgress.lastHeardDb);
        } else if (targetProgress.volIndex >= dBLevels.length - 1) {
          finalizeFrequency(dBLevels[dBLevels.length - 1]);
        } else {
          targetProgress.mode = "ascending";
          targetProgress.volIndex += 1;
        }
      } else if (targetProgress.volIndex >= dBLevels.length - 1) {
        finalizeFrequency(dBLevels[dBLevels.length - 1]);
      } else {
        targetProgress.volIndex += 1;
      }
    } else {
      if (heardAtDb !== undefined && nextResults[targetEar].length === currentEarProgress.freqIndex) {
        nextResults[targetEar].push(heardAtDb);
      }

      if (heardAtDb !== undefined || targetProgress.volIndex >= dBLevels.length - 1) {
        targetProgress.freqIndex += 1;
        targetProgress.volIndex = startingVolumeIndex;
      } else {
        targetProgress.volIndex += 1;
      }
    }

    setResultsByEar(nextResults);
    setProgressByEar(nextProgress);

    const completedCurrentFrequency = nextProgress[targetEar].freqIndex > currentEarProgress.freqIndex;

    const isComplete = randomMode
      ? nextProgress.left.freqIndex >= frequencies.length && nextProgress.right.freqIndex >= frequencies.length
      : nextProgress[targetEar].freqIndex >= frequencies.length;

    if (isComplete) {
      stopActiveSources();
      setIsFinished(true);
      setTimeout(() => {
        onComplete(nextResults);
      }, 300);
      return;
    }

    if (randomMode && professionalMode && !completedCurrentFrequency) {
      setActiveEar(targetEar);
      return;
    }

    const nextEar = pickNextEar(nextProgress);
    if (nextEar !== null) {
      setActiveEar(nextEar);
    }
  }, [frequencies.length, frequencyOrderByEar, onComplete, pickNextEar, professionalMode, progressByEar, randomMode, resultsByEar, startingVolumeIndex, stopActiveSources]);

  // 🔊 play tone (stereo) — intermittent or continuous, with optional masking noise
  const playTone = useCallback(
    (targetEar: Ear, freq: number, volume: number, stepDuration: number = STEP_DURATION) => {
      const ctx = audioCtxRef.current;
      if (!ctx) return;

      const tonePan = targetEar === "left" ? -1 : 1;
      const tonePanner = new StereoPannerNode(ctx, { pan: tonePan });
      tonePanner.connect(ctx.destination);

      if (continuous) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = "sine";
        gain.gain.value = volume;
        osc.connect(gain).connect(tonePanner);
        osc.start();
        osc.stop(ctx.currentTime + stepDuration);
        activeSourcesRef.current.push(osc);
      } else {
        // Intermittent: PULSE_ON / PULSE_OFF pattern
        let t = ctx.currentTime;
        while (t + PULSE_ON <= ctx.currentTime + stepDuration) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.value = freq;
          osc.type = "sine";
          gain.gain.value = volume;
          osc.connect(gain).connect(tonePanner);
          osc.start(t);
          osc.stop(t + PULSE_ON);
          activeSourcesRef.current.push(osc);
          t += PULSE_ON + PULSE_OFF;
        }
      }

    },
    [continuous]
  );

  const heard = () => {
    if (isFinished || stepHandledRef.current) return;

    stepHandledRef.current = true;
    setIsHeardButtonHidden(true);
    stopActiveSources();
    setTimeout(() => {
      stepHandledRef.current = false;
      commitStep(activeEar, dBLevels[currentVolIndex]);
    }, HEARD_PAUSE_DURATION);
  };

  const notHeard = () => {
    if (isFinished || stepHandledRef.current) return;

    stepHandledRef.current = true;
    setIsHeardButtonHidden(true);
    stopActiveSources();
    setTimeout(() => {
      stepHandledRef.current = false;
      commitStep(activeEar);
    }, HEARD_PAUSE_DURATION);
  };

  const handleCancel = () => {
    stopActiveSources();
    stopNoise();
    onCancel(resultsByEar);
  };

  // ⌨️ keyboard shortcuts: Space/Enter = "Słyszę", Esc/N = "Nie słyszę"
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isHeardButtonHidden) return;

      if (e.code === "Space" || e.code === "Enter" || e.key === " " || e.key === "Enter") {
        e.preventDefault();
        heard();
      } else if (e.code === "Escape" || e.key === "Escape" || e.key.toLowerCase() === "n") {
        e.preventDefault();
        notHeard();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }); // no deps — always bind latest heard/notHeard closures

  // 🔊 init AudioContext
  useEffect(() => {
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;

    return () => {
      void ctx.close();
    };
  }, []);  

  // 🔁 auto step: 3s tone + 1.5s pause
  useEffect(() => {
    if (isFinished) return; // ✅ stop po zakończeniu

    if (currentFreqIndex >= frequencies.length) {
      return;
    }

    const stepDuration = currentDbLevel === 0 ? 1 : STEP_DURATION;
    const timer = setTimeout(() => {
      if (!stepHandledRef.current) {
        commitStep(activeEar);
      }
    }, stepDuration * 1000 + PAUSE_DURATION);

    return () => clearTimeout(timer);
  }, [activeEar, commitStep, currentDbLevel, currentFreqIndex, isFinished, frequencies.length]);

  // � continuous masking noise in the contralateral ear
  useEffect(() => {
    if (!addNoise || isFinished) return;

    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const noisePan = activeEar === "left" ? 1 : -1;
    const sampleRate = ctx.sampleRate;
    const bufferSize = Math.ceil(sampleRate * 5);
    const buffer = ctx.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = buffer;
    noiseSource.loop = true;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.1;
    const noisePanner = new StereoPannerNode(ctx, { pan: noisePan });
    noiseSource.connect(noiseGain).connect(noisePanner).connect(ctx.destination);
    noiseSource.start();
    noiseSourceRef.current = noiseSource;

    return () => {
      try { noiseSource.stop(); } catch { /* already stopped */ }
      noiseSourceRef.current = null;
    };
  }, [addNoise, activeEar, isFinished]);

  // �🔊 odtwarzanie
  useEffect(() => {
    if (isFinished) return;

    if (currentFreqIndex >= frequencies.length) {
      return;
    }

    stopActiveSources();
    const stepDuration = currentDbLevel === 0 ? 1 : STEP_DURATION;
    playTone(activeEar, currentFrequency, volumes[currentVolIndex], stepDuration);

    return () => stopActiveSources();
  }, [activeEar, currentDbLevel, currentFreqIndex, currentFrequency, currentVolIndex, frequencies.length, playTone, isFinished, stopActiveSources]);

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="test-panel__content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
          <div className="test-panel__summary">
            <PillTitle as="div" variant="blue-darkner" iconClassName="bi bi-soundwave" text={t("Test tonu")} />
            <h2 className="mb-1">
              {randomMode
                ? t("Losowy test")
                : `${t("Test ucha:")} ${ear === "left" ? t("Lewe") : t("Prawe")}`}
            </h2>
            <p className="mb-0">
              {t("Kliknij tylko wtedy, gdy faktycznie słyszysz sygnał.")}
            </p>
            <p className="mb-0 text_color_lightgreen">
              {t('Gdy usłyszysz dźwięk, naciśnij „Słyszę”. Jeśli nie słyszysz dźwięku, naciśnij „Nie słyszę” lub poczekaj, aż dźwięk się pojawi.')}
            </p>
            {user?.admin == true && (
              <div className="d-flex flex-wrap gap-2 mt-3">
                <span className="badge rounded-pill text-bg-light">
                  {t("Częstotliwość:")} {currentFrequency} Hz
                </span>
                <span className="badge rounded-pill text-bg-light">
                  {t("Poziom głośności:")} {currentDbLevel} dB
                </span>
              </div>
            )}
        </div>

          <div className="text-md-end">
            <span className="small text-uppercase">{t("Postęp")}</span>{" "}
            <strong>{Math.round(progress)}%</strong>
          </div>
        </div>

        <div className="test-panel__center-area">
          <div className="progress-bar">
            <div style={{ width: `${progress}%` }} />
          </div>

          <div className="test-panel__notice" role="note">
            <div className="test-panel__notice-title">
              <i className="bi bi-info-circle-fill" aria-hidden="true" />
              <span>{t("PAMIĘTAJ !")}</span>
            </div>
            <p className="test-panel__notice-text mb-0">
              {t("Badanie ma charakter wyłącznie informacyjny. Nie stanowi podstawy do diagnozy ani leczenia medycznego. W celu potwierdzenia wyników zalecamy umówienie się na profesjonalne badanie słuchu w naszym gabinecie, które jest wykonywane na certyfikowanym sprzęcie medycznym, co zapewnia wysoką precyzję i wiarygodność wyników.")}
            </p>
          </div>
        </div>
        </div>

        <div className="d-flex flex-column flex-sm-row justify-content-end gap-3">
          <div className="d-flex gap-2 flex-grow-1">
            <HearingTestButton type="button" className="btn btn-outline-light btn-lg text-danger" iconClassName="bi bi-x-circle-fill" onClick={notHeard} style={{ visibility: isHeardButtonHidden ? "hidden" : undefined }}>
              {t("Nie słyszę")} (Esc/N)
            </HearingTestButton>
            <HearingTestButton type="button" className="btn btn-outline-light btn-lg fs-4 flex-grow-1" iconClassName="bi bi-ear-fill" onClick={heard} style={{ visibility: isHeardButtonHidden ? "hidden" : undefined }}>
              {t("Słyszę")} (Space/Enter)
            </HearingTestButton>
          </div>
          <HearingTestButton type="button" className="btn btn-outline-warning btn-lg fs-4" iconClassName="bi bi-stop-circle-fill" onClick={handleCancel}>
            {t("Zakończ test")}
          </HearingTestButton>
        </div>
      </div>
    </div>
  );
}